'use client';

import { useCallback, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import ChatBubble from './ChatBubble';
import { AVALANCHE } from '../data/messages';

/**
 * SCENE OVERFLOW — слайд 02.
 * Фон: 80 баблов в masonry-сетке, появляются stagger-fade при попадании
 *  в viewport. Каждый баббл "дышит" — лёгкое y-bobbing с разными амплитудами
 *  и фазами, экран ощущается живым потоком.
 * Под ними — radial vignette, центр светлее.
 * В центре: glass-card с bookmark-плашкой сверху, заголовок-вопрос
 *  и CTA "Да, давайте!" → smooth-scroll на #scene-mascot.
 */

const COUNT = 80;

// Простой детерминированный PRNG для стабильных параметров между SSR/CSR.
function mulberry32(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Spec = {
  text: string;
  floatDuration: number;
  floatDelay: number;
  floatAmplitude: number;
  rotation: number;
};

function buildSpecs(): Spec[] {
  const rand = mulberry32(13);
  const list: Spec[] = [];
  for (let i = 0; i < COUNT; i++) {
    list.push({
      text: AVALANCHE[i % AVALANCHE.length],
      floatDuration: 5 + rand() * 4,        // 5..9 sec
      floatDelay: rand() * 3,                // 0..3 sec
      floatAmplitude: 3 + rand() * 5,        // 3..8 px
      rotation: (rand() - 0.5) * 4,          // -2..+2 deg
    });
  }
  return list;
}

export default function SceneOverflow() {
  const reduced = useReducedMotion() ?? false;
  const specs = useMemo(buildSpecs, []);

  const goToMascot = useCallback(() => {
    document.getElementById('scene-mascot')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section
      id="scene-overflow"
      className="relative min-h-screen w-full overflow-hidden bg-[#f3fcf0]"
    >
      {/* Сетка-фон */}
      <div className="absolute inset-0 px-3 sm:px-6 py-6 sm:py-8 overflow-hidden">
        <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-3 sm:gap-4">
          {specs.map((spec, i) => (
            <FloatingBubble key={i} spec={spec} index={i} reduced={reduced} />
          ))}
        </div>
      </div>

      {/* Radial vignette */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(243,252,240,0.85) 0%, rgba(243,252,240,0.35) 35%, rgba(13,26,12,0.20) 100%)',
        }}
      />

      {/* Центральная glass-карточка */}
      <motion.div
        initial={reduced ? false : { opacity: 0, scale: 0.94, y: 16 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.7, delay: reduced ? 0 : 1.0, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 min-h-screen flex items-center justify-center px-5 sm:px-6"
      >
        <div
          className="relative bg-white/90 backdrop-blur-xl rounded-[2rem] px-7 py-9 sm:px-12 sm:py-11 max-w-md w-full text-center border border-white"
          style={{
            boxShadow:
              '0 40px 100px -28px rgba(20,40,20,0.5), 0 0 0 1px rgba(255,255,255,0.6) inset',
          }}
        >
          <div
            aria-hidden
            className="absolute -top-7 left-1/2 -translate-x-1/2 w-14 h-14 rounded-2xl bg-[#b7102a] flex items-center justify-center text-white"
            style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.5)' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5 3a2 2 0 0 0-2 2v17l9-5 9 5V5a2 2 0 0 0-2-2H5z" />
            </svg>
          </div>

          <h2
            className="font-display font-bold text-[#161d16] tracking-[-0.02em] leading-[1.0] mt-6 mb-8"
            style={{ fontSize: 'clamp(1.75rem, 4.5vw, 2.75rem)' }}
          >
            Разберём это <span className="text-[#b7102a]">кладбище ссылок и текстов</span>?
          </h2>

          <button
            type="button"
            onClick={goToMascot}
            className="group relative inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
            style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.45)' }}
          >
            <span>Да, давайте!</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
            <span aria-hidden className="absolute inset-0 rounded-2xl bg-[#b7102a] opacity-0 group-hover:opacity-30 blur-xl transition-opacity -z-10" />
          </button>
        </div>
      </motion.div>
    </section>
  );
}

/* ─── FloatingBubble: каждый баббл с reveal + бесконечным "дыханием" ─── */

function FloatingBubble({ spec, index, reduced }: { spec: Spec; index: number; reduced: boolean }) {
  if (reduced) {
    return (
      <div className="break-inside-avoid mb-3 sm:mb-4">
        <ChatBubble text={spec.text} variant="received" />
      </div>
    );
  }

  return (
    <motion.div
      className="break-inside-avoid mb-3 sm:mb-4 will-change-transform"
      initial={{ opacity: 0, y: 16, scale: 0.94 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{
        duration: 0.5,
        delay: Math.min(index * 0.014, 1.0),
        ease: [0.16, 1, 0.3, 1],
      }}
      style={{ rotate: spec.rotation }}
    >
      <motion.div
        animate={{ y: [-spec.floatAmplitude, spec.floatAmplitude, -spec.floatAmplitude] }}
        transition={{
          duration: spec.floatDuration,
          delay: spec.floatDelay,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <ChatBubble text={spec.text} variant="received" />
      </motion.div>
    </motion.div>
  );
}
